import styled from "@emotion/styled";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import { Chip } from "@mui/material";
import SelectField from "components/SelectField";
import get from "lodash.get";
import moment from "moment";
import PropTypes from "prop-types";
import React from "react";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";

import ContractUtils from "services/contract/utils";
import CustomAnalysisActioner from "services/customAnalysis/actioner";
import CustomAnalysisUtils from "services/customAnalysis/utils";

import ParkkiTheme from "themes/ParkkiTheme";

import Button from "components/Button";
import Section from "components/Section";
import StatsChart from "components/StatsChart";
import StatsRow from "components/StatsRow";
import ToggleButtonGroup from "components/ToggleButtonGroup";

import { CustomAnalysisFiltersDialog } from "scenes/CustomAnalysis/components";
import { GraphProvider } from "services/graphs";

const StyledContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: ${ParkkiTheme.colors.lightGrey};
`;

const GraphsContainer = styled.div`
  display: flex;
  display: 1;
  flex-direction: row;
`;

const LeftContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  max-width: 50%;
`;
const RightContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  max-width: 50%;
`;

const SubtitleContainer = styled.div`
  display: flex;
`;

const LabelsContainer = styled.div`
  max-width: 300px;
  margin-right: 30px;
`;

const ComparedSubtitleContainer = styled.div`
  background-color: ${ParkkiTheme.colors.orange};
  color: white;
  padding: 0px 4px;
  margin: 3px 0px;
`;

const SubtitleContainerFirstLine = styled.div`
  background-color: ${ParkkiTheme.colors.lightBlue};
  color: white;
  padding: 0px 4px;
  margin: 3px 0px;
`;

const StyledButton = styled(Button)`
  height: 50%;
`;

const ToggleButtonGroupContainer = styled.div`
  display: flex;
  flex: 1;
  margin-bottom: 10px;
`;

const AnalyzedAreasContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-right: 30px;
  margin-top: 5px;
`;

const hourlyAttendanceDaysMap = {
  weekDays: [1, 2, 3, 4, 5],
  weekEnd: [6, 7],
  all: [1, 2, 3, 4, 5, 6, 7],
};

class CustomAnalysis extends React.Component {
  state = {
    filtersDialogOpen: false,
    previousFilters: {},
  };

  componentDidMount() {
    setTimeout(this.initializeModuleData, 200);
  }

  componentDidUpdate(prevProps) {
    const { contract } = this.props;

    if (
      get(prevProps, "contract.id") &&
      get(prevProps, "contract.id") !== get(contract, "id")
    ) {
      setTimeout(this.initializeModuleData, 200);
    }
  }

  initializeModuleData = async () => {
    const { retrieveAllStats, retrieveLayersInfos } = this.props;

    await retrieveLayersInfos();
    await retrieveAllStats();
  };

  openDialog = () => {
    const { filters } = this.props;
    this.setState({ previousFilters: filters, filtersDialogOpen: true });
  };

  hideDialogAndResetFilters = () => {
    const { setFilters } = this.props;
    const { previousFilters } = this.state;

    setFilters(previousFilters);
    this.setState({ filtersDialogOpen: false });
  };

  handleDateChange = (key, date) => {
    const { endDate, startDate } = this.props;

    if (key === "from") {
      if (date.isAfter(moment(endDate), "day"))
        this.updateDate("to", date.endOf("day"));
      return this.updateDate("from", date.startOf("day"));
    }

    if (key === "to") {
      if (date.isBefore(moment(startDate), "day"))
        this.updateDate("from", date.startOf("day"));
      return this.updateDate("to", date.endOf("day"));
    }

    if (key === "comparedFrom") {
      return this.updateDate("comparedFrom", date.startOf("day"));
    }
  };

  updateDate = (key, date) => {
    const { setFilter } = this.props;
    const timestamp = parseInt(date.valueOf() / 1000);
    return setFilter(key, timestamp);
  };

  handleComparisonChange = (value) => {
    const { setFilter } = this.props;
    setFilter("withComparison", value);
  };

  renderStatsRow = () => {
    const { customAnalysis, intl } = this.props;
    const statsRow = CustomAnalysisUtils.getStatsRow(customAnalysis, intl);

    if (!statsRow) return null;

    return (
      <Section withoutTitle={true}>
        <StatsRow
          initialized={get(customAnalysis, "attendance.initialized", false)}
          stats={statsRow}
          numberOfSection={3}
        />
      </Section>
    );
  };

  renderCustomAnalysis = () => {
    const { customAnalysis, intl, contract, startDate, endDate, filters } =
      this.props;
    const dowAttendanceStats = CustomAnalysisUtils.getChartByDayOfWeek(
      "dowAttendance",
      customAnalysis,
      intl
    );
    const dowPressureStats = CustomAnalysisUtils.getChartByDayOfWeek(
      "dowPressure",
      customAnalysis,
      intl
    );
    const selectedAreasLabel = filters?.areas
      ?.filter((area) => filters.selectedAreas.includes(area.id))
      .map((area) => area.long_name)
      .join(", ");

    return (
      <GraphsContainer>
        <LeftContainer>
          {this.renderStatsRow()}
          {this.renderAttendanceGraph()}
          {this.renderAttendancePeakGraph()}
          {this.renderHourlyAttendanceGraph()}
        </LeftContainer>
        <RightContainer>
          {get(dowAttendanceStats, "chart.toggle", false) && (
            <GraphProvider
              timeTitle={`[${ContractUtils.formatTimestamp(
                contract,
                startDate / 1000
              )}]-[${ContractUtils.formatTimestamp(contract, endDate / 1000)}]`}
              label={selectedAreasLabel}
            >
              <Section
                title={
                  intl.messages[
                    "customAnalysisScene.sections.dowAttendance.title"
                  ]
                }
                infoTooltip={
                  intl.messages[
                    "customAnalysisScene.sections.dowAttendance.tooltip"
                  ]
                }
              >
                <StatsChart
                  initialized={customAnalysis.dowAttendance.initialized}
                  stats={dowAttendanceStats.chart}
                  graphOptions={dowAttendanceStats.options}
                />
              </Section>
            </GraphProvider>
          )}

          {get(dowPressureStats, "chart.toggle", false) && (
            <GraphProvider
              timeTitle={`[${ContractUtils.formatTimestamp(
                contract,
                startDate / 1000
              )}]-[${ContractUtils.formatTimestamp(contract, endDate / 1000)}]`}
              label={selectedAreasLabel}
            >
              <Section
                title={
                  intl.messages[
                    "customAnalysisScene.sections.dowPressure.title"
                  ]
                }
                infoTooltip={
                  intl.messages[
                    "customAnalysisScene.sections.dowPressure.tooltip"
                  ]
                }
              >
                <StatsChart
                  initialized={customAnalysis.dowAttendance.initialized}
                  stats={dowPressureStats.chart}
                  graphOptions={dowPressureStats.options}
                />
              </Section>
            </GraphProvider>
          )}
        </RightContainer>
      </GraphsContainer>
    );
  };

  getAnalysisAreaGroups = () => {
    const { filters } = this.props;
    return filters?.analysisAreaGroups;
  };

  renderAnalysisAreaGroupsSelectedIcon = (areaID) => {
    const { filters } = this.props;

    const selectedGroup = filters?.areas?.find(
      (area) => area.id === areaID
    )?.type;

    if (!selectedGroup) return null;
    if (["PARKANDFLOW", "PARK"].includes(selectedGroup))
      return <DirectionsCarIcon fontSize="small" />;
    if (["PEDESTRIANANDFLOW"].includes(selectedGroup))
      return <DirectionsWalkIcon fontSize="small" />;
    return null;
  };

  getAreas = () => {
    const { filters } = this.props;

    return get(filters, "areas", []).map((area) => {
      return {
        ...area,
        text: get(
          area,
          "long_name",
          get(area, "short_name", get(area, "id", ""))
        ),
        value: get(area, "id", ""),
        selected: get(filters, "selectedAreas", []).indexOf(area.id) >= 0,
      };
    });
  };

  renderAttendanceGraph = () => {
    const {
      attendanceFilters,
      customAnalysis,
      intl,
      setAttendanceFilters,
      contract,
      startDate,
      endDate,
      filters,
    } = this.props;

    const attendanceStats = CustomAnalysisUtils.getAttendanceChart(
      "attendance",
      get(attendanceFilters, "dimension"),
      customAnalysis,
      intl
    );

    const options = [
      { label: intl.messages["calendar.day"], value: "days" },
      { label: intl.messages["calendar.week"], value: "weeks" },
      { label: intl.messages["calendar.month"], value: "months" },
    ];

    const selectedAreasLabel = filters?.areas
      ?.filter((area) => filters.selectedAreas.includes(area.id))
      .map((area) => area.long_name)
      .join(", ");

    return (
      get(attendanceStats, "chart.toggle", false) && (
        <GraphProvider
          timeTitle={`[${ContractUtils.formatTimestamp(
            contract,
            startDate / 1000
          )}]-[${ContractUtils.formatTimestamp(contract, endDate / 1000)}]`}
          label={selectedAreasLabel}
        >
          <Section
            title={
              intl.messages["customAnalysisScene.sections.attendance.title"]
            }
            infoTooltip={
              intl.messages["customAnalysisScene.sections.attendance.tooltip"]
            }
          >
            <ToggleButtonGroupContainer>
              <ToggleButtonGroup
                exclusive
                loading={!customAnalysis.attendance.initialized}
                options={options}
                value={get(attendanceFilters, "dimension")}
                onChange={(e, value) => {
                  if (value === null) return;
                  return setAttendanceFilters({ dimension: value });
                }}
              />
            </ToggleButtonGroupContainer>

            <StatsChart
              initialized={customAnalysis.attendance.initialized}
              stats={attendanceStats.chart}
              graphOptions={attendanceStats.options}
              height={"250px"}
            />
          </Section>
        </GraphProvider>
      )
    );
  };

  renderAttendancePeakGraph = () => {
    const {
      attendancePeakFilters,
      customAnalysis,
      intl,
      setAttendancePeakFilters,
      contract,
      startDate,
      endDate,
      filters,
    } = this.props;

    const attendancePeakStats = CustomAnalysisUtils.getAttendanceChart(
      "attendancePeak",
      get(attendancePeakFilters, "dimension"),
      customAnalysis,
      intl
    );

    const options = [
      { label: intl.messages["calendar.day"], value: "days" },
      { label: intl.messages["calendar.week"], value: "weeks" },
      { label: intl.messages["calendar.month"], value: "months" },
    ];

    const selectedAreasLabel = filters?.areas
      ?.filter((area) => filters.selectedAreas.includes(area.id))
      .map((area) => area.long_name)
      .join(", ");

    return (
      get(attendancePeakStats, "chart.toggle", false) && (
        <GraphProvider
          timeTitle={`[${ContractUtils.formatTimestamp(
            contract,
            startDate / 1000
          )}]-[${ContractUtils.formatTimestamp(contract, endDate / 1000)}]`}
          label={selectedAreasLabel}
        >
          <Section
            title={
              intl.messages["customAnalysisScene.sections.attendancePeak.title"]
            }
            infoTooltip={
              intl.messages[
                "customAnalysisScene.sections.attendancePeak.tooltip"
              ]
            }
          >
            <ToggleButtonGroupContainer>
              <ToggleButtonGroup
                exclusive
                loading={!customAnalysis.attendancePeak.initialized}
                options={options}
                value={get(attendancePeakFilters, "dimension")}
                onChange={(e, value) => {
                  if (value === null) return;
                  return setAttendancePeakFilters({ dimension: value });
                }}
              />
            </ToggleButtonGroupContainer>
            <StatsChart
              initialized={customAnalysis.attendancePeak.initialized}
              stats={attendancePeakStats.chart}
              graphOptions={attendancePeakStats.options}
              height={"250px"}
            />
          </Section>
        </GraphProvider>
      )
    );
  };

  renderHourlyAttendanceGraph = () => {
    const {
      hourlyAttendanceFilters,
      customAnalysis,
      intl,
      setHourlyAttendanceFilters,
      contract,
      startDate,
      endDate,
      filters,
    } = this.props;

    const hourlyAttendanceStats = CustomAnalysisUtils.getAttendanceChart(
      "hourlyAttendance",
      get(hourlyAttendanceFilters, "dimension"),
      customAnalysis,
      intl
    );

    const options = new Array(7).fill(0).map((item, index) => ({
      label: intl.messages[`daysOfWeek.${index}`],
      value: index + 1,
    }));
    const selectedDays =
      get(hourlyAttendanceFilters, "dow") &&
      Array.isArray(get(hourlyAttendanceFilters, "dow"))
        ? get(hourlyAttendanceFilters, "dow")
        : [get(hourlyAttendanceFilters, "dow")];

    // Determine which preset corresponds to the selected days
    const getCurrentPresetValue = () => {
      if (!selectedDays || selectedDays.length === 0) return "all";

      const isArrayEqual = (arr1, arr2) =>
        arr1.length === arr2.length && arr1.every((val) => arr2.includes(val));

      if (isArrayEqual(selectedDays, hourlyAttendanceDaysMap.weekDays))
        return "weekDays";
      if (isArrayEqual(selectedDays, hourlyAttendanceDaysMap.weekEnd))
        return "weekEnd";
      if (isArrayEqual(selectedDays, hourlyAttendanceDaysMap.all)) return "all";

      return ""; // Empty value when selection doesn't match any preset
    };

    const selectedAreasLabel = filters?.areas
      ?.filter((area) => filters.selectedAreas.includes(area.id))
      .map((area) => area.long_name)
      .join(", ");

    return (
      get(hourlyAttendanceStats, "chart.toggle", false) && (
        <GraphProvider
          timeTitle={`[${ContractUtils.formatTimestamp(
            contract,
            startDate / 1000
          )}]-[${ContractUtils.formatTimestamp(contract, endDate / 1000)}]`}
          label={selectedAreasLabel}
        >
          <Section
            title={
              intl.messages[
                "customAnalysisScene.sections.hourlyAttendance.title"
              ]
            }
            infoTooltip={
              intl.messages[
                "customAnalysisScene.sections.hourlyAttendance.tooltip"
              ]
            }
          >
            <ToggleButtonGroupContainer>
              <ToggleButtonGroup
                loading={!customAnalysis.hourlyAttendance.initialized}
                options={options}
                value={selectedDays || []}
                onChange={(e, value) => {
                  if (
                    value === null ||
                    (Array.isArray(value) && value.length === 0)
                  )
                    return;
                  return setHourlyAttendanceFilters({ dow: value });
                }}
                customSelector={
                  <SelectField
                    size="small"
                    variant="outlined"
                    label={
                      intl.messages[
                        "customAnalysisScene.sections.hourlyAttendance.customSelector.label"
                      ]
                    }
                    value={getCurrentPresetValue()}
                    values={[
                      {
                        text: intl.messages[
                          "customAnalysisScene.sections.hourlyAttendance.customSelector.workingDays"
                        ],
                        value: "weekDays",
                      },
                      {
                        text: intl.messages[
                          "customAnalysisScene.sections.hourlyAttendance.customSelector.weekEnd"
                        ],
                        value: "weekEnd",
                      },
                      {
                        text: intl.messages[
                          "customAnalysisScene.sections.hourlyAttendance.customSelector.all"
                        ],
                        value: "all",
                      },
                    ]}
                    onChange={(x) => {
                      const value = x.target.value;
                      if (value === "") return; // Don't change anything for empty value
                      setHourlyAttendanceFilters({
                        dow: hourlyAttendanceDaysMap[value],
                      });
                    }}
                    disabled={!customAnalysis.hourlyAttendance.initialized}
                  />
                }
              />
            </ToggleButtonGroupContainer>
            <StatsChart
              initialized={customAnalysis.hourlyAttendance.initialized}
              stats={hourlyAttendanceStats.chart}
              graphOptions={hourlyAttendanceStats.options}
              height={"330px"}
            />
          </Section>
        </GraphProvider>
      )
    );
  };

  getSelectedAreas() {
    const { filters } = this.props;
    const { selectedAreas, areas } = filters;

    return selectedAreas
      .map((selectedArea) => areas.find((area) => area.id === selectedArea))
      .filter((area) => area !== undefined);
  }

  render() {
    const {
      contract,
      endDate,
      intl,
      filters,
      retrieveAllStats,
      setFilter,
      startDate,
      comparedStartDate,
      withComparison,
    } = this.props;
    const { filtersDialogOpen } = this.state;
    const comparedTo = CustomAnalysisUtils.getComparedTo(
      startDate,
      endDate,
      comparedStartDate
    );
    const selectedAreas = this.getSelectedAreas();

    return (
      <StyledContainer>
        <Section
          style={{ marginBottom: 0 }}
          title={intl.messages["customAnalysisScene.title"]}
          subtitle={
            <>
              <SubtitleContainer>
                <LabelsContainer>
                  <div>
                    <SubtitleContainerFirstLine>
                      {intl.formatMessage(
                        {
                          id: "flowScene.flowSection.subtitle",
                        },
                        {
                          from: moment(startDate).format("D MMMM YYYY"),
                          to: moment(endDate).format("D MMMM YYYY"),
                        }
                      )}
                    </SubtitleContainerFirstLine>
                  </div>
                  {withComparison ? (
                    <ComparedSubtitleContainer>
                      {intl.formatMessage(
                        {
                          id: "flowScene.flowSection.comparedSubtitle",
                        },
                        {
                          from: moment(comparedStartDate).format("D MMMM YYYY"),
                          to: moment(comparedTo).format("D MMMM YYYY"),
                        }
                      )}
                    </ComparedSubtitleContainer>
                  ) : null}
                </LabelsContainer>

                <StyledButton
                  fullWidth={false}
                  label={intl.messages["customAnalysisScene.updatePeriod"]}
                  onClick={this.openDialog}
                />
              </SubtitleContainer>
              {selectedAreas?.length >= 1 && (
                <AnalyzedAreasContainer>
                  <strong style={{ marginTop: -8 }}>
                    {`${intl.messages["customAnalysisScene.filters.analyzedAreas"]}: `}
                  </strong>
                  <span style={{ marginLeft: 2 }}>
                    <>
                      {selectedAreas.map((area) => {
                        return (
                          <Chip
                            key={area.id}
                            label={area?.long_name}
                            avatar={this.renderAnalysisAreaGroupsSelectedIcon(
                              area.id
                            )}
                            sx={{ marginRight: 1, marginBottom: 1 }}
                          />
                        );
                      })}
                    </>
                  </span>
                </AnalyzedAreasContainer>
              )}
            </>
          }
        >
          <CustomAnalysisFiltersDialog
            open={filtersDialogOpen}
            setFilter={setFilter}
            hideDialog={this.hideDialogAndResetFilters}
            analysisAreaGroups={this.getAnalysisAreaGroups()}
            areas={this.getAreas()}
            withComparison={{
              value: withComparison,
              onChange: (e, checked) => this.handleComparisonChange(checked),
            }}
            startDate={{
              value: startDate,
              label: intl.messages["customAnalysisScene.filters.date.start"],
              onChange: (date) => this.handleDateChange("from", date),
              maxDaysAllowed:
                ContractUtils.getCustomAnalysisMaxDaysAllowed(contract),
            }}
            endDate={{
              value: endDate,
              label: intl.messages["customAnalysisScene.filters.date.end"],
              onChange: (date) => this.handleDateChange("to", date),
            }}
            comparedStartDate={{
              value: comparedStartDate,
              label: intl.messages["customAnalysisScene.filters.date.start"],
              onChange: (date) => this.handleDateChange("comparedFrom", date),
            }}
            comparedEndDate={{
              value: comparedTo,
            }}
            search={{
              label: intl.messages["components.buttons.search"],
              onClick: () =>
                this.setState(
                  { filtersDialogOpen: false, previousFilters: filters },
                  () => {
                    return retrieveAllStats();
                  }
                ),
            }}
          />
        </Section>
        {this.renderCustomAnalysis()}
      </StyledContainer>
    );
  }
}

const mapStateToProps = (state) => ({
  contract: state.Contract.contract,
  customAnalysis: state.CustomAnalysis,
  filters: state.CustomAnalysis.filters,
  startDate: get(state.CustomAnalysis, "filters.from") * 1000,
  endDate: get(state.CustomAnalysis, "filters.to") * 1000,
  withComparison: get(state.CustomAnalysis, "filters.withComparison"),
  comparedStartDate: get(state.CustomAnalysis, "filters.comparedFrom") * 1000,
  attendanceFilters: get(state.CustomAnalysis, "attendance.filters"),
  attendancePeakFilters: get(state.CustomAnalysis, "attendancePeak.filters"),
  hourlyAttendanceFilters: get(
    state.CustomAnalysis,
    "hourlyAttendance.filters"
  ),
});

const mapDispatchToProps = (dispatch) => ({
  retrieveAllStats: () => dispatch(CustomAnalysisActioner.retrieveAllStats()),
  retrieveLayersInfos: () =>
    dispatch(CustomAnalysisActioner.retrieveLayersInfos()),
  setFilters: (value) => dispatch(CustomAnalysisActioner.setFilters(value)),
  setFilter: (key, value) =>
    dispatch(CustomAnalysisActioner.setFilter(key, value)),
  setAttendanceFilters: (value) =>
    dispatch(CustomAnalysisActioner.setAttendanceFilters(value)),
  setAttendancePeakFilters: (value) =>
    dispatch(CustomAnalysisActioner.setAttendancePeakFilters(value)),
  setHourlyAttendanceFilters: (value) =>
    dispatch(CustomAnalysisActioner.setHourlyAttendanceFilters(value)),
});

CustomAnalysis.defaultProps = {};

CustomAnalysis.propTypes = {
  contract: PropTypes.object.isRequired,
  endDate: PropTypes.number.isRequired,
  customAnalysis: PropTypes.object.isRequired,
  retrieveAllStats: PropTypes.func.isRequired,
  startDate: PropTypes.number.isRequired,
};

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(CustomAnalysis)
);
